import React, { useEffect, useRef, useState } from 'react';
import { COLORS, DEFAULT_COLOR, DEFAULT_HIGHLIGHT_MODE, DEFAULT_TOOL, HIGHLIGHT_MODES, TOOLS, export_filename, mount_annotator } from './annotateImage';
import FaIcon from '../FaIcon';

/**
 * Thin React wrapper over annotateImage.js (the dependency-free canvas
 * module paired with packages/support-panel/src/annotate/ in the frontend
 * monorepo — the module is shared, only this wrapper is app-specific).
 *
 * Full-screen layer over a source image: tool + colour toolbar (Box · Arrow
 * · Draw · Highlight · Text · Blur), Undo, Cancel (Esc) and Done. The
 * toolbar mirrors the controller's state through on_change — picking
 * Highlight switches to the marker colour (yellow by default) and shows the
 * box / free sub-toggle. Done exports a PNG at the image's natural
 * resolution and hands `on_done({ blob, file, filename })` to the caller —
 * the thread then uploads it as an annotated reply to the original message.
 *
 * Props: src (same-origin/blob URL), filename, on_done, on_cancel.
 */

// Each tool's face: a Font Awesome icon (FaIcon's name / regular), never a symbol character a system may have no
// glyph for (T004, CT-15747); Text keeps its plain "T".
const TOOL_LABELS = {
    rect: [{ name: 'square', regular: true }, 'Box'], arrow: [{ name: 'location-arrow' }, 'Arrow'], freehand: [{ name: 'pen' }, 'Draw'],
    highlight: [{ name: 'highlighter' }, 'Highlight'], text: ['T', 'Text'], blur: [{ name: 'th' }, 'Blur'],
};
const HIGHLIGHT_MODE_LABELS = { rect: [{ name: 'square', regular: true }, 'box'], freehand: [{ name: 'signature' }, 'free'] };
const face = (g) => (typeof g === 'string' ? g : <FaIcon name={g.name} regular={g.regular} />);

export default function AnnotateImage({ src, filename, on_done, on_cancel }) {
    const host_ref = useRef(null);
    const ctl_ref = useRef(null);
    const [phase, set_phase] = useState('loading'); // loading | ready | exporting | failed
    const [tool, set_tool] = useState(DEFAULT_TOOL);
    const [color, set_color] = useState(DEFAULT_COLOR);
    const [hl_mode, set_hl_mode] = useState(DEFAULT_HIGHLIGHT_MODE);
    const [undoable, set_undoable] = useState(false);
    const on_cancel_ref = useRef(on_cancel);
    on_cancel_ref.current = on_cancel;

    useEffect(() => {
        let dead = false;
        const img = new Image();
        img.onload = () => {
            if (dead || !host_ref.current) return;
            try {
                ctl_ref.current = mount_annotator(host_ref.current, img, {
                    on_change: (s) => {
                        // The controller owns tool / colour / marker mode; the toolbar follows it.
                        set_tool(s.tool);
                        set_color(s.color);
                        set_hl_mode(s.highlight_mode);
                        set_undoable(!!(s.draft || s.shapes.length));
                    },
                    on_cancel: () => { if (on_cancel_ref.current) on_cancel_ref.current(); },
                });
                set_phase('ready');
            } catch (e) { set_phase('failed'); }
        };
        img.onerror = () => { if (!dead) set_phase('failed'); };
        img.src = src;
        return () => {
            dead = true;
            if (ctl_ref.current) { ctl_ref.current.destroy(); ctl_ref.current = null; }
        };
    }, [src]);

    const pick_tool = (t) => { if (ctl_ref.current) ctl_ref.current.set_tool(t); };
    const pick_color = (c) => { if (ctl_ref.current) ctl_ref.current.set_color(c); };
    const pick_hl_mode = (m) => { if (ctl_ref.current) ctl_ref.current.set_highlight_mode(m); };
    const undo = () => { if (ctl_ref.current) ctl_ref.current.undo(); };
    const done = async () => {
        if (!ctl_ref.current || phase !== 'ready') return;
        set_phase('exporting');
        try {
            const blob = await ctl_ref.current.export();
            const name = export_filename(filename);
            const file = typeof File === 'function' ? new File([blob], name, { type: 'image/png' }) : blob;
            on_done({ blob, file, filename: name });
        } catch (e) {
            set_phase('ready');
            window.alert('Could not export the annotated image: ' + ((e && e.message) || 'unknown error'));
        }
    };

    const btn = (active) => ({
        border: '1px solid ' + (active ? '#fff' : 'rgba(255,255,255,0.25)'), background: active ? 'rgba(255,255,255,0.18)' : 'transparent',
        color: '#fff', borderRadius: 8, padding: '4px 10px', fontSize: 13, cursor: 'pointer', lineHeight: '18px', minWidth: 34,
    });
    const sub_btn = (active) => ({
        border: '1px solid ' + (active ? '#fdd835' : 'rgba(255,255,255,0.25)'), background: active ? 'rgba(253,216,53,0.18)' : 'transparent',
        color: '#fff', borderRadius: 6, padding: '2px 7px', fontSize: 11, cursor: 'pointer', lineHeight: '16px',
    });
    return (
        <div onClick={(e) => e.stopPropagation()} role='dialog' aria-label='Annotate image'
            style={{ position: 'fixed', inset: 0, background: 'rgba(12,16,20,0.94)', zIndex: 2100, display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', flexWrap: 'wrap', background: 'rgba(0,0,0,0.35)' }}>
                <b style={{ color: '#fff', fontSize: 13, marginRight: 8 }}>Annotate</b>
                {TOOLS.map((t) => (
                    <React.Fragment key={t}>
                        <button type='button' title={TOOL_LABELS[t][1]} onClick={() => pick_tool(t)} style={btn(tool === t)} disabled={phase !== 'ready'}>
                            {face(TOOL_LABELS[t][0])} <span style={{ fontSize: 11, opacity: 0.85 }}>{TOOL_LABELS[t][1]}</span>
                        </button>
                        {t === 'highlight' && tool === 'highlight' ? (
                            // Marker sub-mode: a translucent box, or a freehand marker stroke.
                            <span style={{ display: 'inline-flex', gap: 3, marginRight: 4 }} title='Highlight as a box or a freehand marker stroke'>
                                {HIGHLIGHT_MODES.map((m) => (
                                    <button key={m} type='button' onClick={() => pick_hl_mode(m)} style={sub_btn(hl_mode === m)} disabled={phase !== 'ready'}>
                                        {face(HIGHLIGHT_MODE_LABELS[m][0])} {HIGHLIGHT_MODE_LABELS[m][1]}
                                    </button>
                                ))}
                            </span>
                        ) : null}
                    </React.Fragment>
                ))}
                <span style={{ width: 1, height: 18, background: 'rgba(255,255,255,0.25)', margin: '0 4px' }} />
                {Object.keys(COLORS).map((c) => (
                    <button key={c} type='button' title={c} onClick={() => pick_color(c)} disabled={phase !== 'ready'}
                        style={{ width: 24, height: 24, borderRadius: 12, background: COLORS[c], border: '2px solid ' + (color === c ? '#fff' : 'transparent'), cursor: 'pointer', padding: 0 }} />
                ))}
                <span style={{ width: 1, height: 18, background: 'rgba(255,255,255,0.25)', margin: '0 4px' }} />
                <button type='button' onClick={undo} style={btn(false)} disabled={!undoable || phase !== 'ready'} title='Undo (Ctrl+Z)'><FaIcon name='undo' /> Undo</button>
                <span style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
                    <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11 }}>{phase === 'exporting' ? 'exporting…' : 'Esc cancels'}</span>
                    <button type='button' onClick={on_cancel} style={btn(false)} disabled={phase === 'exporting'}>Cancel</button>
                    <button type='button' onClick={done} disabled={phase !== 'ready'}
                        style={{ ...btn(false), background: '#2f6fed', border: '1px solid #2f6fed', fontWeight: 600, padding: '4px 16px' }}>
                        Done <FaIcon name='check' />
                    </button>
                </span>
            </div>
            <div style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
                {phase === 'failed' ? <span style={{ color: '#e0888b', fontSize: 13 }}>Could not load the image.</span> : null}
                {phase === 'loading' ? <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13, position: 'absolute' }}>loading image…</span> : null}
                <div ref={host_ref} style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }} />
            </div>
        </div>
    );
}
